import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DollarSign, Save } from "lucide-react";
import { API_BASE } from "./AuthContext";

const SOURCE_LABEL = {
  email: "Email", whatsapp: "WhatsApp", phone: "Phone", referral: "Referral",
  website_form: "Website form", walk_in: "Walk-in", viber: "Viber", manual: "Manual",
};

function fmtMoneyShort(v) {
  if (!v) return "$0";
  return v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v.toFixed(0)}`;
}

/**
 * Card on /reports that lets owners log monthly marketing spend per lead source
 * and shows the resulting ROI alongside the existing source attribution data.
 */
export default function MarketingRoiCard({ days, sources, isAdmin }) {
  const [spendRows, setSpendRows] = useState([]);
  const [editing, setEditing] = useState({});         // source → in-progress value
  const [busy, setBusy] = useState({});               // source → bool
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addSource, setAddSource] = useState("");

  const fetchSpend = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/marketing-spend`);
      setSpendRows(r.data?.rows || []);
    } catch {
      // Non-admin users get an empty card; that's fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSpend(); }, []);

  const save = async (source) => {
    const raw = editing[source];
    if (raw === undefined) return;
    const value = parseFloat(raw);
    if (Number.isNaN(value) || value < 0) {
      toast.error("Monthly cost must be a non-negative number");
      return;
    }
    setBusy((b) => ({ ...b, [source]: true }));
    try {
      await axios.put(`${API_BASE}/marketing-spend`, {
        source, monthly_cost: value,
      });
      toast.success(`Spend for ${SOURCE_LABEL[source] || source} updated`);
      setEditing((e) => { const n = { ...e }; delete n[source]; return n; });
      await fetchSpend();
      // Trigger /reports to reload sources (parent listens via event)
      window.dispatchEvent(new Event("reports:reload"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, [source]: false }));
    }
  };

  // Join: sources (with ROI from /api/reports/source-attribution) + spend rows
  const sourcesBySrc = Object.fromEntries((sources?.sources || []).map((r) => [r.source, r]));
  const rows = spendRows
    .map((sp) => ({ ...sp, ...(sourcesBySrc[sp.source] || {}) }))
    // Show only sources that either have spend OR have leads in the window,
    // OR were just added by the owner via "Add source".
    .filter((r) => (r.monthly_cost || 0) > 0 || (r.count || 0) > 0 || editing[r.source] !== undefined);
  // Sort: configured spend first, then by count
  rows.sort((a, b) => (b.monthly_cost || 0) - (a.monthly_cost || 0) || (b.count || 0) - (a.count || 0));

  // Sources currently hidden (zero spend AND no leads AND not actively editing)
  const hiddenSources = spendRows
    .filter((sp) => (sp.monthly_cost || 0) === 0
                     && (sourcesBySrc[sp.source]?.count || 0) === 0
                     && editing[sp.source] === undefined)
    .map((sp) => sp.source);

  return (
    <div className="card p-5" data-testid="marketing-roi-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline inline-flex items-center gap-1"><DollarSign size={12}/> Marketing ROI · {days}d</div>
          <div className="text-[16px] font-medium">Spend vs. won value</div>
        </div>
        <div className="text-[11px] text-[var(--ink-3)]">ROI = (won − spend) ÷ spend</div>
      </div>
      {loading ? (
        <div className="text-[var(--ink-3)] text-[13px]">Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] border-b" style={{ borderColor: "var(--border)" }}>
              <tr>
                <th className="text-left py-1.5 pr-2">Source</th>
                <th className="text-right py-1.5 px-2">Monthly $</th>
                <th className="text-right py-1.5 px-2">Window $</th>
                <th className="text-right py-1.5 px-2">Won $</th>
                <th className="text-right py-1.5 pl-2">ROI</th>
                {isAdmin && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody data-testid="marketing-roi-rows">
              {rows.map((r) => {
                const editingValue = editing[r.source];
                const isEditing = editingValue !== undefined;
                const roiPct = r.roi_window == null ? null : Math.round(r.roi_window * 100);
                const roiColor = roiPct == null ? "var(--ink-3)" :
                                  roiPct >= 100 ? "var(--moss)" :
                                  roiPct >= 0 ? "var(--ink)" : "var(--accent)";
                return (
                  <tr key={r.source} className="border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`marketing-roi-row-${r.source}`}>
                    <td className="py-2 pr-2 text-[var(--ink)]">{SOURCE_LABEL[r.source] || r.source}</td>
                    <td className="py-2 px-2 text-right">
                      {isAdmin ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={isEditing ? editingValue : (r.monthly_cost || 0)}
                          onChange={(e) => setEditing((s) => ({ ...s, [r.source]: e.target.value }))}
                          className="w-20 text-right px-2 py-1 rounded border bg-transparent font-mono text-[12px]"
                          style={{ borderColor: "var(--border)" }}
                          data-testid={`marketing-roi-input-${r.source}`}
                        />
                      ) : (
                        <span className="font-mono">{fmtMoneyShort(r.monthly_cost || 0)}</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-[var(--ink-2)]">{fmtMoneyShort(r.spend_window || 0)}</td>
                    <td className="py-2 px-2 text-right font-mono text-[var(--ink-2)]">{fmtMoneyShort(r.won_value || 0)}</td>
                    <td className="py-2 pl-2 text-right font-mono" style={{ color: roiColor }}>
                      {roiPct == null ? "—" : `${roiPct}%`}
                    </td>
                    {isAdmin && (
                      <td className="py-2 pl-2 text-right">
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => save(r.source)}
                            disabled={busy[r.source]}
                            data-testid={`marketing-roi-save-${r.source}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded disabled:opacity-50"
                            style={{ background: "var(--ink)", color: "var(--paper)" }}
                            title="Save"
                          >
                            <Save size={12} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="text-[var(--ink-3)] text-[13px] py-4 text-center">No marketing spend logged yet — use <strong>+ Add source</strong> below to seed one.</div>
          )}
          {isAdmin && hiddenSources.length > 0 && (
            <div className="mt-3 pt-3 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }} data-testid="marketing-roi-add-row">
              {adding ? (
                <>
                  <select
                    value={addSource}
                    onChange={(e) => setAddSource(e.target.value)}
                    data-testid="marketing-roi-add-source-select"
                    className="px-2 py-1.5 rounded border bg-transparent text-[12px]"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <option value="">— Pick a source —</option>
                    {hiddenSources.map((src) => (
                      <option key={src} value={src}>{SOURCE_LABEL[src] || src}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      if (!addSource) return;
                      setEditing((s) => ({ ...s, [addSource]: "0" }));
                      setAddSource("");
                      setAdding(false);
                    }}
                    disabled={!addSource}
                    data-testid="marketing-roi-add-confirm"
                    className="px-2.5 py-1.5 rounded text-[12px] font-medium disabled:opacity-50"
                    style={{ background: "var(--ink)", color: "var(--paper)" }}
                  >Add</button>
                  <button
                    type="button"
                    onClick={() => { setAdding(false); setAddSource(""); }}
                    className="px-2 py-1.5 text-[11px] text-[var(--ink-3)]"
                  >Cancel</button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  data-testid="marketing-roi-add-open"
                  className="text-[11px] text-[var(--ink-2)] hover:text-[var(--ink)] underline"
                >+ Add source</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
